/**
 * THE PARTS EVERY SCREEN IS BUILT FROM — CTS 1's parts (D260).
 *
 * A KPI tile, an attention card, a register table, a field, a pill, a banner,
 * the scan control, the queue panel. They are here once so that ten screens
 * cannot drift into ten dialects, and they are drawn the way CTS 1 draws them
 * because the staff already read that language.
 *
 * Accessibility is not a layer on top: every field has a visible label and
 * ties its error to itself with `aria-describedby`, every refusal is announced
 * in a live region, status is never colour alone (the word is always there),
 * and every action is a real button.
 */
import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { money, count, words } from './format.js';
import { go } from './hooks.js';

// ---------------------------------------------------------------- semantic colour
export const COLOURS = {
  money: 'var(--money)', primary: 'var(--primary)', danger: 'var(--danger)',
  good: 'var(--good)', empty: 'var(--empty)', testing: 'var(--testing)', muted: 'var(--muted)',
} as const;
export type Tone = keyof typeof COLOURS;

/** CTS 1's colour for a state word — and the word itself is always shown. */
export function toneOf(state: string | null | undefined): Tone {
  switch ((state ?? '').toLowerCase()) {
    case 'filled_in_plant': case 'delivered': case 'approved': case 'active': case 'won': return 'good';
    case 'empty_in_plant': case 'returned': case 'pending': return 'empty';
    case 'with_customer': case 'departed': case 'submitted': case 'issued': return 'primary';
    case 'overdue': case 'rejected': case 'refused': case 'lost': case 'shortage': case 'cancelled': return 'danger';
    case 'testing': case 'quarantine': case 'superseded': case 'withdrawn': return 'testing';
    default: return 'muted';
  }
}

export function Pill({ children, tone }: { children: string | null | undefined; tone?: Tone }) {
  const c = COLOURS[tone ?? toneOf(children)];
  return <span className="pill" style={{ color: c, background: `color-mix(in srgb, ${c} 12%, white)` }}>{words(children)}</span>;
}

export function Banner({ kind, title, detail, reference }: { kind: 'error' | 'warn' | 'info' | 'good'; title: string; detail?: string; reference?: string }) {
  const mark = kind === 'error' ? '!' : kind === 'warn' ? '!' : kind === 'good' ? '✓' : 'i';
  return (
    <div className={`banner ${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <span className="mark" style={{ background: 'currentColor', color: '#fff' }}>{mark}</span>
      <span>
        {title}
        {detail ? <span className="detail">{detail}</span> : null}
        {reference ? <span className="detail">Reference {reference} — quote this if you report it.</span> : null}
      </span>
    </div>
  );
}

export function Kpi({ label, value, note, tone, href }: { label: string; value: string; note?: string; tone?: Tone; href?: string }) {
  const body = (
    <div className="kpi" style={{ ['--accent' as string]: COLOURS[tone ?? 'primary'] }}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value" {...(/^[—–-]$/.test(value.trim()) ? { 'data-empty': 'true' } : {})}>{value}</div>
      {note ? <div className="kpi-note">{note}</div> : null}
    </div>
  );
  // a tile that counts work is a way to that work (audit H5, 22 Sep)
  if (!href) return body;
  return <a className="kpi-link" href={href} onClick={(e: { preventDefault: () => void }) => { e.preventDefault(); go(href); }}>{body}</a>;
}

export function Card({ title, sub, actions, children, foot, className }:
  { title?: string; sub?: React.ReactNode; actions?: React.ReactNode; children?: React.ReactNode; foot?: React.ReactNode;
    /**
     * An extra class on the section, for a panel a test needs to NAME.
     *
     * The browser suite waited for a serial to disappear from the whole page BODY to decide
     * that a row had left a queue. On a database with more in it the serial appeared
     * elsewhere too — the register below it — so the wait could never succeed and reported
     * sixty seconds of nothing. A test should be able to ask about one panel.
     */
    className?: string }) {
  return (
    <section className={className ? `card ${className}` : 'card'}>
      {title ? (
        <div className="card-head">
          <div><h2>{title}</h2>{sub ? <div className="sub">{sub}</div> : null}</div>
          {actions ? <div className="page-actions">{actions}</div> : null}
        </div>
      ) : null}
      <div className="card-body">{children}</div>
      {foot ? <div className="card-foot">{foot}</div> : null}
    </section>
  );
}

export function EmptyState({ title, detail }: { title: string; detail?: string }) {
  return <div className="empty-state"><strong>{title}</strong>{detail ? <span>{detail}</span> : null}</div>;
}

// ---------------------------------------------------------------- the register
export interface Column<R> {
  key: string;
  head: string;
  /** right-align a number, a quantity or money */
  right?: boolean;
  cell: (row: R) => React.ReactNode;
}

export function Register<R>({ columns, rows, empty, onOpen, totals, asOf }: {
  columns: readonly Column<R>[];
  rows: readonly R[];
  empty: string;
  onOpen?: (row: R) => void;
  totals?: React.ReactNode;
  /** every register says what it counted and when (the register rules) */
  asOf?: string;
}) {
  if (rows.length === 0) return <EmptyState title={empty} />;
  return (
    <>
      <div className="table-wrap">
        <table className="register">
          <thead>
            <tr>{columns.map((c) => <th key={c.key} className={c.right ? 'right' : undefined} scope="col">{c.head}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} data-clickable={onOpen ? 'yes' : 'no'}
                  onClick={onOpen ? () => onOpen(row) : undefined}
                  tabIndex={onOpen ? 0 : undefined}
                  onKeyDown={onOpen ? (e: { key: string }) => { if (e.key === 'Enter' || e.key === ' ') onOpen(row); } : undefined}>
                {columns.map((c) => <td key={c.key} className={c.right ? 'right num' : undefined}>{c.cell(row)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(totals || asOf) ? (
        <div className="card-foot" style={{ marginTop: 12, borderRadius: 10, border: '1px solid var(--border)' }}>
          <div className="totals">
            {totals}
            {asOf ? <span className="muted">as of {asOf}</span> : null}
          </div>
        </div>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------- fields
export function Field({ label, name, error, help, children }:
  { label: string; name: string; error?: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={name}>{label}</label>
      {children}
      {help ? <div className="help" id={`${name}-help`}>{help}</div> : null}
      {error ? <div className="bad" id={`${name}-error`}>{error}</div> : null}
    </div>
  );
}

export function Text({ name, value, onChange, label, error, help, type = 'text', placeholder, required, autoFocus }: {
  name: string; value: string; onChange: (v: string) => void; label: string;
  error?: string; help?: string; type?: 'text' | 'password' | 'date' | 'number' | 'month'; placeholder?: string; required?: boolean; autoFocus?: boolean;
}) {
  return (
    <Field label={label} name={name} {...(error ? { error } : {})} {...(help ? { help } : {})}>
      <input id={name} name={name} type={type} value={value} required={required} autoFocus={autoFocus}
             placeholder={placeholder ?? ''}
             aria-invalid={error ? 'true' : undefined}
             aria-describedby={[help ? `${name}-help` : '', error ? `${name}-error` : ''].filter(Boolean).join(' ') || undefined}
             onChange={(e: { target: { value: string } }) => onChange(e.target.value)} />
    </Field>
  );
}

export function Select({ name, value, onChange, label, options, error, help, placeholder }: {
  name: string; value: string; onChange: (v: string) => void; label: string;
  options: readonly { value: string; label: string }[]; error?: string; help?: string; placeholder?: string;
}) {
  return (
    <Field label={label} name={name} {...(error ? { error } : {})} {...(help ? { help } : {})}>
      <select id={name} name={name} value={value}
              aria-invalid={error ? 'true' : undefined}
              aria-describedby={[help ? `${name}-help` : '', error ? `${name}-error` : ''].filter(Boolean).join(' ') || undefined}
              onChange={(e: { target: { value: string } }) => onChange(e.target.value)}>
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

export function TextArea({ name, value, onChange, label, error, help, placeholder }: {
  name: string; value: string; onChange: (v: string) => void; label: string; error?: string; help?: string; placeholder?: string;
}) {
  return (
    <Field label={label} name={name} {...(error ? { error } : {})} {...(help ? { help } : {})}>
      <textarea id={name} name={name} value={value} placeholder={placeholder ?? ''}
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={[help ? `${name}-help` : '', error ? `${name}-error` : ''].filter(Boolean).join(' ') || undefined}
                onChange={(e: { target: { value: string } }) => onChange(e.target.value)} />
    </Field>
  );
}

export function Check({ name, checked, onChange, label }: { name: string; checked: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <div className="field checkline">
      <input id={name} name={name} type="checkbox" checked={checked}
             onChange={(e: { target: { checked: boolean } }) => onChange(e.target.checked)} />
      <label htmlFor={name} style={{ textTransform: 'none', letterSpacing: 0, fontSize: 13.5, color: 'var(--ink)', fontWeight: 600, margin: 0 }}>{label}</label>
    </div>
  );
}

/** The yard's control: scan icon, one input, one Add — as CTS 1 draws it. */
export function ScanBox({ label, value, onChange, onAdd, help, busy }: {
  label: string; value: string; onChange: (v: string) => void; onAdd: () => void; help?: string; busy?: boolean;
}) {
  return (
    <Field label={label} name="scan" {...(help ? { help } : {})}>
      <div className="scan">
        <span className="mark" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3M4 12h16" />
          </svg>
        </span>
        <input id="scan" name="scan" type="text" value={value} placeholder="Type or scan serial…" autoComplete="off"
               onChange={(e: { target: { value: string } }) => onChange(e.target.value)}
               onKeyDown={(e: { key: string; preventDefault: () => void }) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(); } }} />
        <button type="button" onClick={onAdd} disabled={busy}>Add</button>
      </div>
    </Field>
  );
}

/** CTS1's filter pills: one row, one choice, the chosen one filled (DIR / TYPE / STATUS on the old register). */
export function Seg({ name, value, onChange, options }: {
  name: string; value: string; onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
}) {
  return (
    <div className="seg" role="group" aria-label={name}>
      {options.map((o) => (
        <button key={o.value} type="button" className={`seg-btn${value === o.value ? ' on' : ''}`}
                aria-pressed={value === o.value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

export function Money({ paise, plain }: { paise: number | null | undefined; plain?: boolean }) {
  return <span className={plain ? 'num' : 'money'}>{money(paise)}</span>;
}
export function Count({ n }: { n: number | null | undefined }) { return <span className="num">{count(n)}</span>; }
export function Serial({ children }: { children: string | null | undefined }) { return <span className="serial">{children ?? '—'}</span>; }

/**
 * THE PICKER — a chooser that is not capped at two hundred.
 *
 * Every chooser in this application asked its view for `pageSize: 200`, and
 * 200 is `PAGE_SIZE_MAX` — the largest page the door will give. Past two
 * hundred customers the rest did not exist as far as the screen was
 * concerned: no error, no "show more", simply absent. On thirteen screens.
 * The owner's words were "many cylinders dont even show up, no drop down",
 * and the dispatch screen's own placeholder read "Search or select customer…"
 * over a control that offered no search at all.
 *
 * The search happens on the SERVER, which is what removes the ceiling —
 * nothing is held here, and a query matching three thousand rows returns its
 * first page with a true total. "Show more" reaches the rest. The keyboard
 * reaches everything the mouse can. A slow answer cannot overwrite a newer
 * one.
 *
 * Drawn with this application's own tokens, not a second dialect.
 */
export function Picker<T>({
  name, label, value, onChange, search, render, keyOf, help, error, placeholder,
}: {
  name: string; label: string;
  value: T | null;
  onChange: (v: T | null) => void;
  /** asks the server; `page` is 1-based */
  search: (q: string, page: number) => Promise<{ rows: T[]; total: number }>;
  render: (row: T) => string;
  keyOf: (row: T) => string | number;
  help?: string; error?: string; placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [hover, setHover] = useState(0);
  const root = useRef<{ contains: (t: unknown) => boolean } | null>(null);
  const box = useRef<{ focus: () => void } | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return;
    const mine = ++seq.current;
    setBusy(true);
    const t = setTimeout(() => {
      void search(q.trim(), page).then(
        (r) => {
          if (seq.current !== mine) return;      // a newer query already answered
          setRows((old) => (page === 1 ? r.rows : [...old, ...r.rows]));
          setTotal(r.total); setBusy(false);
        },
        () => { if (seq.current === mine) setBusy(false); },
      );
    }, 150);
    return () => clearTimeout(t);
  }, [q, page, open]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) setTimeout(() => box.current?.focus(), 10); }, [open]);
  useEffect(() => { setPage(1); setHover(0); }, [q]);
  useEffect(() => {
    if (!open) return;
    const away = (e: { target: unknown }) => { if (root.current && !root.current.contains(e.target)) setOpen(false); };
    globalThis.document?.addEventListener('mousedown', away as never);
    return () => globalThis.document?.removeEventListener('mousedown', away as never);
  }, [open]);

  const take = (row: T) => { onChange(row); setOpen(false); setQ(''); };
  const more = Math.max(0, total - rows.length);

  return (
    <Field label={label} name={name} {...(error ? { error } : {})} {...(help ? { help } : {})}>
      <div ref={root as never} style={{ position: 'relative' }}>
        <button type="button" id={name} className="picker-trigger" aria-expanded={open} aria-haspopup="listbox"
                onClick={() => setOpen((v) => !v)}>
          <span className={value === null ? 'muted' : ''}>{value === null ? (placeholder ?? 'Search or select…') : render(value)}</span>
          <span aria-hidden="true">▾</span>
        </button>
        {open ? (
          <div className="picker-pop">
            <input ref={box as never} type="search" value={q} placeholder="Type to search…" aria-label={`Search ${label}`}
                   onChange={(e: { target: { value: string } }) => setQ(e.target.value)}
                   onKeyDown={(e: { key: string; preventDefault: () => void }) => {
                     if (e.key === 'ArrowDown') { e.preventDefault(); setHover((h) => Math.min(h + 1, rows.length - 1)); }
                     else if (e.key === 'ArrowUp') { e.preventDefault(); setHover((h) => Math.max(h - 1, 0)); }
                     else if (e.key === 'Enter') { e.preventDefault(); const r = rows[hover]; if (r) take(r); }
                     else if (e.key === 'Escape') { setOpen(false); }
                   }} />
            <div className="picker-list" role="listbox">
              {rows.length === 0 && !busy ? (
                <div className="picker-empty">{q.trim() ? `Nothing matches “${q.trim()}”.` : 'Nothing to choose yet.'}</div>
              ) : rows.map((r, i) => (
                <div key={keyOf(r)} role="option" aria-selected={i === hover}
                     className={`picker-row${i === hover ? ' on' : ''}`}
                     onMouseEnter={() => setHover(i)}
                     onMouseDown={(e: { preventDefault: () => void }) => { e.preventDefault(); take(r); }}>
                  {render(r)}
                </div>
              ))}
              {busy ? <div className="picker-empty">Looking…</div> : null}
              {more > 0 ? (
                <button type="button" className="picker-more" onClick={() => setPage((n) => n + 1)}>
                  Show more — {count(more)} not shown
                </button>
              ) : null}
            </div>
            <div className="picker-foot">
              {total > 0 ? <span>Showing {count(rows.length)} of {count(total)}</span> : <span />}
              {value !== null ? <button type="button" className="picker-clear" onClick={() => { onChange(null); setOpen(false); }}>Clear</button> : null}
            </div>
          </div>
        ) : null}
      </div>
    </Field>
  );
}

export function Button({ children, onClick, kind = 'ghost', disabled, wide, small, type = 'button' }: {
  children: React.ReactNode; onClick?: () => void; kind?: 'primary' | 'ghost' | 'good' | 'danger';
  disabled?: boolean; wide?: boolean; small?: boolean; type?: 'button' | 'submit';
}) {
  return (
    <button type={type} className={`btn ${kind}${wide ? ' wide' : ''}${small ? ' small' : ''}`} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

/** What a screen shows while it is asking the server. */
export function Loading({ what }: { what: string }) {
  return <div className="empty-state" role="status"><strong>Loading {what}…</strong></div>;
}

/**
 * 11 Oct — CTS1's numbered form section: "1 · Offer — number is assigned by
 * the system when you save". One component so every long form reads the same.
 */
export function Section({ n, title, desc, children }: { n: number | string; title: string; desc?: string; children: React.ReactNode }) {
  return (
    <section className="sec" data-section={n}>
      <div className="sec-head"><span className="sec-n">{n}</span><span className="sec-title">{title}</span>{desc ? <span className="sec-desc">{desc}</span> : null}</div>
      {children}
    </section>
  );
}
